import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Navigate,
	Route,
	Routes,
	useLocation,
	useNavigate,
} from "react-router-dom";
import { Provider as BusProvider } from "react-bus";
import {
	Alert,
	AlertColor,
	Box,
	Button,
	CircularProgress,
	IconButton,
	Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AppContext } from "./Contexts/AppContext";
import { UserSessionDto } from "./models/User";
import UserApi from "./api/user";
import {
	getUserPermissionariesIds,
	getUserRoleForPermissionary,
} from "./utils/userPermissionaries";
import NavigationBar from "./common/components/NavigationBar";
import DevicesScreen from "./screens/DevicesScreen/DevicesScreen";
import DeviceDetailsScreen from "./screens/DeviceDetailsScreen/DeviceDetailsScreen";
import LoginScreen from "./screens/LoginScreen/LoginScreen";
import UsersScreen from "./screens/UsersScreen/UsersScreen";
import NotificationsScreen from "./screens/NotificationsScreen/NotificationsScreen";
import DeviceHistoryScreen from "./screens/DeviceDetailsScreen/DeviceHistoryScreen/DeviceHistoryScreen";
import ConnectivityReportScreen from "./screens/ConnectivityReportScreen/ConnectivityReportScreen";
import PermissionaryConfigurationScreen from "./screens/PermissionaryConfigurationScreen/PermissionaryConfigurationScreen";
import LandingScreen from "./screens/LandingScreen/LandingScreen";

const publicRoutes = new Set(["/", "/login"]);

function EntryApp() {
	const navigate = useNavigate();
	const location = useLocation();
	const [title, setTitle] = React.useState("Devices");
	const [userContext, setUserContext] = React.useState<UserSessionDto | null>(
		null
	);
	const [authResolved, setAuthResolved] = useState(false);

	const [snackBar, setSnackBar] = useState({
		open: false,
		message: "",
		autoHideDuration: 6000,
		severity: "success" as AlertColor,
		onClose: (event: React.SyntheticEvent | Event) => handleClose(event),
	});

	const isPublicRoute = publicRoutes.has(location.pathname);

	const appContextValue = {
		title,
		setTitle,
		userContext,
		setUserContext,
		snackBar,
		setSnackBar,
	};

	const handleClose = (
		event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}

		setSnackBar((currentSnackBar) => ({ ...currentSnackBar, open: false }));
	};

	const snackBarNode = useMemo(
		() => (
			<Snackbar
				open={snackBar.open}
				autoHideDuration={snackBar.autoHideDuration}
				onClose={handleClose}
				message={snackBar.message}
				action={
					<React.Fragment>
						<Button color='secondary' size='small' onClick={handleClose}>
							UNDO
						</Button>
						<IconButton
							size='small'
							aria-label='close'
							color='inherit'
							onClick={handleClose}>
							<CloseIcon fontSize='small' />
						</IconButton>
					</React.Fragment>
				}>
				<Alert severity={snackBar.severity}>{snackBar.message}</Alert>
			</Snackbar>
		),
		[snackBar]
	);

	const fetchUser = useCallback(async () => {
		const token = localStorage.getItem("accessToken");
		if (!token) {
			setUserContext(null);
			setAuthResolved(true);
			return;
		}

		try {
			const session = (await UserApi.getMe({
				token,
			})) as UserSessionDto;
			const { accessToken, userId, permissionaryName, permissionaryRoles } =
				session;
			const permissionariesIds = getUserPermissionariesIds(session);
			const selectedPermissionaryId = permissionariesIds[0] || "";
			const selectedRole = getUserRoleForPermissionary(
				session,
				selectedPermissionaryId
			);

			if (!accessToken) {
				throw new Error("No token");
			}

			localStorage.setItem("accessToken", accessToken);
			setUserContext({
				accessToken,
				userId,
				role: selectedRole,
				permissionariesIds,
				permissionaryRoles,
				permissionaryId: selectedPermissionaryId,
				permissionaryName,
			});
		} catch (exception: any) {
			console.error("logging error ", exception);
			localStorage.removeItem("accessToken");
			setUserContext(null);
			if (!publicRoutes.has(location.pathname)) {
				navigate("/login", { replace: true });
			}
		} finally {
			setAuthResolved(true);
		}
	}, [location.pathname, navigate]);

	useEffect(() => {
		fetchUser();
	}, [fetchUser]);

	const privateRoutes = (
		<NavigationBar>
			<Routes>
				<Route path='/devices' element={<DevicesScreen />} />
				<Route path='/devices/:deviceId' element={<DeviceDetailsScreen />} />
				<Route path='/users' element={<UsersScreen />} />
				<Route path='/device-history/:id' element={<DeviceHistoryScreen />} />
				<Route
					path='/connectivity-report'
					element={<ConnectivityReportScreen />}
				/>
				<Route path='/notifications' element={<NotificationsScreen />} />
				<Route
					path='/supplier/configuration'
					element={<PermissionaryConfigurationScreen />}
				/>
				<Route path='*' element={<Navigate to='/devices' replace />} />
			</Routes>
		</NavigationBar>
	);

	return (
		<AppContext.Provider value={appContextValue}>
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<BusProvider>
					{!authResolved && !isPublicRoute ? (
						<Box
							sx={{
								minHeight: "100vh",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								background:
									"linear-gradient(135deg, #08121c 0%, #0f2234 100%)",
							}}>
							<CircularProgress />
						</Box>
					) : userContext ? (
						privateRoutes
					) : (
						<Routes>
							<Route path='/' element={<LandingScreen />} />
							<Route path='/login' element={<LoginScreen />} />
							<Route path='*' element={<Navigate to='/' replace />} />
						</Routes>
					)}
					{snackBarNode}
				</BusProvider>
			</LocalizationProvider>
		</AppContext.Provider>
	);
}

export default EntryApp;
